/**
 * Structural config for the Core Modules section. Holds only language-invariant
 * data: ids, display codes, technical tags and the canvas scene name per stage.
 * All human-readable text lives in messages/modules/<locale>.json under the
 * `Modules` namespace and is looked up by these ids.
 */

interface ModuleCheck {
	/** Stable id — keys into the localized `check` map. */
	id: string;
	/** Technical mono tag, language-invariant (e.g. OCR_EXTRACT). */
	tag: string;
}

interface ModuleStage {
	/** Stable id — keys into the localized `stage` map. */
	id: string;
	/** Two-digit step number shown in the stepper. */
	code: string;
	/** English mono code shown beside the localized label (e.g. DOCUMENT). */
	en: string;
	/** Canvas scene name passed to <scene-canvas>. */
	scene: string;
	checks: ModuleCheck[];
}

interface ModuleDef {
	/** Stable id — keys into the localized `m` map. */
	id: string;
	/** Subject code shown on the scene panel (e.g. ID-01). */
	code: string;
	/** Status mono tag (e.g. BIOMETRIC_MATCH). */
	tag: string;
	stages: ModuleStage[];
}

/** Bottom "Platform // Make it yours" customization cards (dark block). */
interface CustomCard {
	/** Stable id — keys into the localized `custom.card` map. */
	id: string;
	num: string;
	/** Highlighted tag (the paid add-on). */
	highlight?: boolean;
}

export const CUSTOM_CARDS: CustomCard[] = [
	{ id: 'flow', num: '01' },
	{ id: 'forms', num: '02' },
	{ id: 'wl', num: '03', highlight: true },
	{ id: 'brand', num: '04' },
	{ id: 'domain', num: '05' },
];

export const MODULES: ModuleDef[] = [
	{
		id: 'kyc',
		code: 'ID-01',
		tag: 'BIOMETRIC_MATCH',
		stages: [
			{
				id: 'setup',
				code: '01',
				en: 'SETUP',
				scene: 'setup',
				checks: [
					{ id: 'country', tag: 'COUNTRY_SELECT' },
					{ id: 'otp', tag: 'OTP_VERIFY' },
					{ id: 'custom', tag: 'CUSTOM_FIELD' },
				],
			},
			{
				id: 'document',
				code: '02',
				en: 'DOCUMENT',
				scene: 'kyc-01',
				checks: [
					{ id: 'ocr', tag: 'OCR_EXTRACT' },
					{ id: 'auth', tag: 'AUTHENTICITY' },
					{ id: 'nfc', tag: 'NFC_READ' },
				],
			},
			{
				id: 'biometrics',
				code: '03',
				en: 'BIOMETRICS',
				scene: 'kyc-02',
				checks: [
					{ id: 'live', tag: 'LIVENESS_PASSIVE' },
					{ id: 'fmatch', tag: 'FACE_MATCH' },
					{ id: 'kface', tag: 'KNOWN_FACE' },
				],
			},
			{
				id: 'address',
				code: '04',
				en: 'ADDRESS',
				scene: 'kyc-03',
				checks: [
					{ id: 'poa', tag: 'PROOF_OF_ADDRESS' },
					{ id: 'nondoc', tag: 'NON_DOC' },
					{ id: 'dup', tag: 'DUPLICATE' },
				],
			},
			{
				id: 'card',
				code: '05',
				en: 'CARD',
				scene: 'card',
				checks: [
					{ id: 'pan', tag: 'PAN_CHECK' },
					{ id: 'holder', tag: 'CARDHOLDER' },
					{ id: 'tds', tag: 'CARD_3DS' },
				],
			},
			{
				id: 'device',
				code: '06',
				en: 'DEVICE',
				scene: 'device',
				checks: [
					{ id: 'fp', tag: 'DEVICE_FINGERPRINT' },
					{ id: 'fraud', tag: 'FRAUD_SIGNALS' },
					{ id: 'geoip', tag: 'GEO_IP' },
				],
			},
			{
				id: 'aml',
				code: '07',
				en: 'AML_SCREEN',
				scene: 'kyc-04',
				checks: [
					{ id: 'sanc', tag: 'SANCTIONS_PEP' },
					{ id: 'media', tag: 'ADVERSE_MEDIA' },
					{ id: 'block', tag: 'BLOCKLIST' },
				],
			},
			{
				id: 'monitoring',
				code: '08',
				en: 'MONITORING',
				scene: 'kyc-05',
				checks: [
					{ id: 'caml', tag: 'CONTINUOUS_AML' },
					{ id: 'fmon', tag: 'FRAUD_MONITOR' },
					{ id: 'reuse', tag: 'REUSABLE_KYC' },
				],
			},
		],
	},
	{
		id: 'kyb',
		code: 'BIZ-02',
		tag: 'UBO_DISCOVERY',
		stages: [
			{
				id: 'director',
				code: '01',
				en: 'HYBRID',
				scene: 'hybrid',
				checks: [
					{ id: 'dkyc', tag: 'DIRECTOR_KYC' },
					{ id: 'auth', tag: 'AUTHORITY' },
					{ id: 'role', tag: 'ROLE_LINK' },
				],
			},
			{
				id: 'registry',
				code: '02',
				en: 'REGISTRY',
				scene: 'kyb-01',
				checks: [
					{ id: 'lookup', tag: 'COMPANY_LOOKUP' },
					{ id: 'regdata', tag: 'REG_DATA' },
					{ id: 'docs', tag: 'ALLDOCS' },
				],
			},
			{
				id: 'ownership',
				code: '03',
				en: 'OWNERSHIP',
				scene: 'kyb-02',
				checks: [
					{ id: 'ubo', tag: 'UBO_DISCOVERY' },
					{ id: 'tree', tag: 'OWNERSHIP_TREE' },
					{ id: 'officers', tag: 'OFFICERS' },
				],
			},
			{
				id: 'screening',
				code: '04',
				en: 'SCREENING',
				scene: 'kyb-03',
				checks: [
					{ id: 'esanc', tag: 'ENTITY_SANCTIONS' },
					{ id: 'uboscr', tag: 'UBO_SCREEN' },
					{ id: 'emedia', tag: 'ADVERSE_MEDIA' },
				],
			},
			{
				id: 'risk',
				code: '05',
				en: 'RISK',
				scene: 'kyb-04',
				checks: [
					{ id: 'score', tag: 'RISK_SCORING' },
					{ id: 'geo', tag: 'GEO_INDUSTRY' },
					{ id: 'bank', tag: 'BANK_ACCOUNT' },
				],
			},
			{
				id: 'monitoring',
				code: '06',
				en: 'MONITORING',
				scene: 'kyb-05',
				checks: [
					{ id: 'cmon', tag: 'CONTINUOUS_MON' },
					{ id: 'salert', tag: 'SANCTION_ALERTS' },
					{ id: 'rever', tag: 'RE_VERIFY' },
				],
			},
		],
	},
	{
		id: 'aml',
		code: 'AML-03',
		tag: 'WALLET_RISK',
		stages: [
			{
				id: 'address',
				code: '01',
				en: 'ADDRESS',
				scene: 'crypto',
				checks: [
					{ id: 'wrisk', tag: 'WALLET_RISK' },
					{ id: 'cluster', tag: 'CLUSTERING' },
					{ id: 'asset', tag: 'ASSET' },
				],
			},
			{
				id: 'source',
				code: '02',
				en: 'SOURCE',
				scene: 'crypto',
				checks: [
					{ id: 'tx', tag: 'TX_TRACING' },
					{ id: 'sof', tag: 'SOURCE_OF_FUNDS' },
					{ id: 'expo', tag: 'EXPOSURE' },
				],
			},
			{
				id: 'screening',
				code: '03',
				en: 'SCREENING',
				scene: 'kyc-04',
				checks: [
					{ id: 'ofac', tag: 'SANCTIONS' },
					{ id: 'illicit', tag: 'ILLICIT' },
					{ id: 'block', tag: 'BLOCKLIST' },
				],
			},
			{
				id: 'travel',
				code: '04',
				en: 'TRAVEL_RULE',
				scene: 'kyb-03',
				checks: [
					{ id: 'vasp', tag: 'VASP' },
					{ id: 'ivms', tag: 'IVMS101' },
					{ id: 'cparty', tag: 'COUNTERPARTY' },
				],
			},
			{
				id: 'monitoring',
				code: '05',
				en: 'MONITORING',
				scene: 'kyc-05',
				checks: [
					{ id: 'cont', tag: 'CONTINUOUS' },
					{ id: 'alerts', tag: 'ALERTS' },
					{ id: 'rescr', tag: 'RE_SCREEN' },
				],
			},
		],
	},
];
